import { createSelector } from '@ngrx/store';

import { UserSelectors } from '@recruitee/account-data';
import { CandidateSelectors } from '@recruitee/candidate-data';
import { InterviewEventParams, InterviewEventWidget } from '@recruitee/dashboard-types';
import { InterviewScheduleResultRequestsCollectionSelectors } from '@recruitee/interview-schedules-data';
import {
  createEntityCollectionStore,
  DEFAULT_SCOPE_NAME,
  getEntityCollectionActions,
  memoizeSelector,
  PaginatedActionScopedPayload,
  PickActions,
} from '@recruitee/ngrx';
import { JobItemsSelectors } from '@recruitee/offers-data';

const idSelector = (item: { id: number }) => item.id;
const {
  namespace,
  actions,
  reducer,
  selectors: defaultSelectors,
} = createEntityCollectionStore<InterviewEventWidget>()('InterviewEventWidget', {
  references: makeRef => [
    makeRef('adminIds', 'admins', UserSelectors.byIds),
    makeRef('candidateId', 'candidate', candidateId => CandidateSelectors.byId(candidateId!)!),
    makeRef('offerId', 'offer', offerId => JobItemsSelectors.byId(offerId!)!),
    makeRef(
      'interviewResultRequestsIds',
      'interviewResultRequests',
      InterviewScheduleResultRequestsCollectionSelectors.byIds,
    ),
  ],
  idSelector,
  customActions: adapter => {
    const defaultActions = getEntityCollectionActions(adapter, idSelector);
    return {
      fetchPaginatedCollection: (
        state,
        payload: { params: InterviewEventParams; scope?: string; replace: boolean },
      ) =>
        defaultActions.fetchPaginatedCollection(state, {
          scope: payload.scope || DEFAULT_SCOPE_NAME,
          add: !payload.replace,
          params: payload.params,
        }),
      fetchPaginatedCollectionSuccess: (
        state,
        payload: PaginatedActionScopedPayload<InterviewEventWidget[]>,
      ) => defaultActions.fetchPaginatedCollectionSuccess(state, payload),
    };
  },
});

const selectors = {
  ...defaultSelectors,
  getEntitiesWithReferencesAndPagination: memoizeSelector((scope: string) =>
    createSelector(
      defaultSelectors.pagination({ scope }),
      defaultSelectors.entitiesWithReferences({ scope }),
      (pagination, entities) => ({
        meta: pagination,
        rows: entities,
      }),
    ),
  ),
};

export const InterviewEventWidgetActions: PickActions<typeof actions, 'fetchPaginatedCollection'> =
  actions;

export {
  namespace as InterviewEventWidgetNamespace,
  reducer as InterviewEventWidgetReducer,
  selectors as InterviewEventWidgetSelectors,
};
